import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Client } from "@/api/schemas";
import { ClientBrand } from "./ClientBrand";
import { ClientPicker } from "./ClientPicker";
import styles from "./ClientPickerPopover.module.css";

type Phase = "closed" | "open" | "closing";
const EXIT_MS = 100;

interface ClientPickerPopoverProps {
  clients: readonly Client[];
  actual: Client | null;
  recientes: readonly string[];
  onSelect: (client: Client) => void;
  /** `lateral` en la barra lateral; `cabecera` en la cabecera móvil. */
  variant: "lateral" | "cabecera";
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Selector de cliente anclado a su disparador (interaction.md §2): entra con opacidad + escala 0.95→1
 * (150 ms, `transform-origin` en el disparador) y sale en 100 ms solo con opacidad. El foco va al buscador
 * al abrir y vuelve al disparador con Esc o clic fuera.
 */
export function ClientPickerPopover({ clients, actual, recientes, onSelect, variant }: ClientPickerPopoverProps) {
  const [phase, setPhase] = useState<Phase>("closed");
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const popoverId = useId();
  const name = actual?.name ?? "Elegir cliente";

  const close = useCallback((restoreFocus: boolean) => {
    clearTimeout(timer.current);
    if (prefersReducedMotion()) setPhase("closed");
    else {
      setPhase("closing");
      timer.current = setTimeout(() => setPhase("closed"), EXIT_MS);
    }
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (phase !== "open") return;
    function onPointerDown(event: PointerEvent) {
      if (event.target instanceof Node && !wrapRef.current?.contains(event.target)) close(true);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      close(true);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [phase, close]);

  function toggle() {
    if (phase === "open") close(false);
    else {
      clearTimeout(timer.current);
      setPhase("open");
    }
  }

  function select(client: Client) {
    close(client.slug === actual?.slug);
    if (client.slug !== actual?.slug) onSelect(client);
  }

  return (
    <div ref={wrapRef} className={`${styles.wrap} ${variant === "cabecera" ? styles.wrapCabecera : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${variant === "cabecera" ? styles.triggerCabecera : ""}`}
        aria-haspopup="dialog"
        aria-expanded={phase === "open"}
        aria-controls={phase === "closed" ? undefined : popoverId}
        aria-label={actual ? `Cliente activo: ${name}. Cambiar de cliente` : "Elegir cliente"}
        title={name}
        onClick={toggle}
      >
        {actual ? <ClientBrand client={actual} size={variant === "cabecera" ? 28 : 32} /> : <span className={styles.placeholderAvatar} aria-hidden="true" />}
        <span className={styles.name} data-etiqueta>{name}</span>
        <svg className={styles.chevron} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M4.5 6.5 8 10l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {phase !== "closed" ? (
        <div
          id={popoverId}
          role="dialog"
          aria-label="Cambiar de cliente"
          className={styles.popover}
          data-state={phase}
          aria-hidden={phase === "closing" ? true : undefined}
        >
          <ClientPicker
            clients={clients}
            actualSlug={actual?.slug ?? null}
            recientes={recientes}
            onSelect={select}
            footer={
              <>
                <Link className={styles.footerLink} to="/clientes" onClick={() => close(false)}>Ver todos los clientes</Link>
                <Link className={styles.footerLink} to="/clientes/nuevo" onClick={() => close(false)}>Nuevo cliente</Link>
              </>
            }
          />
        </div>
      ) : null}
    </div>
  );
}
